<template>
  <div
    class="fa-width-auto partner-widget"
    :class="classes"
  >
    <component
      :is="views[screen]"
      v-if="ready && isContentReady"
      class="partner-widget__container"
      v-bind="screenProps"
      v-on="screenEvents"
    />
    <loading-alt
      v-else
      class="partner-widget__container"
    />
    <div id="modals" />
    <the-snackbar class="partner-widget__snackbar" />
  </div>
</template>

<script setup>
  import { computed, defineAsyncComponent, onBeforeMount, ref, watch } from 'vue'

  import useApplication from '@shared/hooks/useApplication'
  import usePartnerWidget from '@PartnerWidget/hooks/usePartnerWidget'
  import usePartnerWidgetBundleLoader from '@PartnerWidget/hooks/usePartnerWidgetBundleLoader'

  import { PACKAGE } from '@shared/config/constants'
  import { SCREEN } from '@PartnerWidget/constants'

  import LoadingAlt from '@shared/components/Container/LoadingAlt'
  import TheSnackbar from '@shared/components/Basic/Snackbar'

  import '@shared/assets/css/import.css'
  import { logError } from '@shared/helpers/Logger'

  // HOOKS
  const { changeLanguage, isContentReady } = useApplication()
  const { getQueryParams } = usePartnerWidget()
  const {
    loadBundle,
    ready,
    screen,
    screensDefinition,
    screensDefinitionInit,
    setBundleId,
    setCustomerTheme,
    theme: bundleTheme,
  } = usePartnerWidgetBundleLoader()

  // INIT
  const views = {
    [SCREEN.BUNDLE]: defineAsyncComponent(() => import('@PartnerWidget/views/Bundle')),
    [SCREEN.DETAIL]: defineAsyncComponent(() => import('@PartnerWidget/views/Detail')),
    [SCREEN.ENTRY]: defineAsyncComponent(() => import('@PartnerWidget/views/Entry')),
    [SCREEN.ERROR]: defineAsyncComponent(() => import('@PartnerWidget/views/Error')),
  }

  // DATA
  const screenEvents = ref({})
  const screenProps = ref({})

  // COMPUTED
  const classes = computed(() => {
    return [`theme-${bundleTheme.value}`, `screen-${screen.value}`]
  })

  const handleDarkTheme = () => {
    if (bundleTheme.value === 'darkgreen') {
      document.body.classList.add('appearance-extended')
    }
  }

  // METHODS
  async function initFromParams() {
    try {
      // Get URL parameters and create a case-insensitive map
      const urlParamsEntries = new URLSearchParams(window.location.search).entries()
      const paramsMap = {}

      // Convert all parameter keys to lowercase for case-insensitive lookup
      for (const [key, value] of urlParamsEntries) {
        paramsMap[key.toLowerCase()] = value
      }

      // Look for parameters in a case-insensitive way
      setBundleId(paramsMap['bundleid'])
      setCustomerTheme(paramsMap['theme'])
      await changeLanguage({
        language: paramsMap['language']?.toUpperCase() ?? 'DE',
        additionalApps: [PACKAGE.PRODUCTS],
      })
    } catch (e) {
      logError(e)
    }
  }

  // WATCHERS
  watch(
    () => screen.value,
    () => {
      if (screen.value) {
        screenEvents.value = screensDefinition.value[screen.value].events
        screenProps.value = screensDefinition.value[screen.value].props
      }
    },
    { deep: true }
  )

  watch(
    () => screensDefinition.value,
    () => {
      screenEvents.value = screensDefinition.value[screen.value].events
      screenProps.value = screensDefinition.value[screen.value].props
    },
    { deep: true }
  )

  // LIFECYCLE HOOKS
  onBeforeMount(async () => {
    await initFromParams()
    screensDefinitionInit()
    loadBundle(getQueryParams())
    handleDarkTheme()
  })
</script>

<style name="base-styles">
/* Breakpoint 3 */
* {
  box-sizing: border-box;
}

:root {
  /*
    New stacking contexts can be formed on an element in one of three ways:
    - When an element is the root element of a document (the <html> element)
    - When an element has a position value other than static and a z-index value other than auto
    - When an element has an opacity value less than 1
  */
  --dvp-stack-level-element: 1;
  --dvp-stack-level-footer: 1;
  --dvp-stack-level-header: 2;
  --dvp-stack-level-popup-menu: 2;
  --dvp-stack-level-modal: 3;
  --dvp-stack-level-modal-stacked: 4; /* exception for medical-office-search modal as it can be opened within another modal */
  --dvp-stack-level-popup-notification: 5;
  --dvp-stack-level-system-notification: 7;
  --dvp-stack-level-screen-blocker: 8;
  color-scheme: var(--color-scheme, 'light');
}
#chat-ui {
  position: relative;
  z-index: var(--dvp-stack-level-popup-menu);
}
*,
*:after,
*:before {
  box-sizing: border-box;
}
*:focus {
  outline: none;
}
html {
  overflow-y: scroll;
}
body {
  font-family: var(--font-family-open-sans);
  font-style: var(--font-style-default);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-regular-lg);
  letter-spacing: var(--letter-spacing-regular-lg);
  color: var(--Text-Primary);
  background-color: var(--surface);
  margin: 0;
  /* FLEX-2954 prevent larger fonts for mobile landscape */
  -webkit-text-size-adjust: none;
}
p,
h1,
h2,
h3 {
  margin-block-start: 0;
  margin-block-end: 0;
}
a {
  color: inherit;
  text-decoration-line: none;
}
button,
input,
select,
textarea {
  font-family:
    Open Sans,
    sans-serif !important;
}
</style>

<style>
.partner-widget {
  font-family: 'Open Sans', sans-serif;
  margin: 0 auto;
  position: relative;

  em {
    font-style: normal;
    color: var(--secondary);
  }
}

.partner-widget__container {
  min-height: 100vh;
  padding: var(--fixed-spacing-fix-04);
}

.partner-widget__snackbar .notification__text {
  font-size: var(--font-size-regular-sm);
}

@media screen and (--v-pawi-w-small) {
  /* Breakpoint 2 */
  .partner-widget__container {
    padding: var(--fixed-spacing-fix-05);
  }

  .partner-widget__snackbar .notification__text {
    font-size: var(--font-size-regular-md);
    line-height: var(--line-height-regular-md);
  }
}
@media screen and (--v-pawi-h-medium) {
  /* Breakpoint 5 */
  .partner-widget__container {
    padding: var(--fixed-spacing-fix-05);
  }

  .partner-widget__snackbar .notification__text {
    font-size: var(--font-size-regular-md);
    line-height: var(--line-height-regular-md);
  }
  @media screen and (--v-pawi-w-small) {
    /* Breakpoint 4 */
    .partner-widget__container {
      padding: var(--fixed-spacing-fix-06);
    }
  }
}
@media screen and (--v-pawi-w-medium) {
  /* Breakpoint 1 */
  .partner-widget__container {
    padding: var(--fixed-spacing-fix-06);
  }
}
</style>

<style name="theme">
/* DARKGREEN THEME */
.theme-darkgreen {
  &.partner-widget {
    background-color: var(--surface-ext-b-low);
  }

  &.partner-widget .entry-title {
    color: var(--tertiary);
  }

  &.partner-widget .badge {
    background-color: var(--surface);
    color: var(--tertiary);
  }

  &.partner-widget .trigger {
    background-color: var(--surface-ext-b-low);
    .trigger-icon {
      color: var(--Interaction-States-enabled-extended);
    }
  }

  &.partner-widget .entry-title em {
    color: #5bac26;
  }

  &.partner-widget .Package .heading h1,
  &.partner-widget .Package .heading .price,
  &.partner-widget .Detail .actions {
    color: var(--on-surface);
  }
}
</style>
